// Small fetch wrapper for Airchain's captions endpoints
// (/t/{tenant_id}/api/channels/{channelid}/captions...), called from
// Captions.js. Airchain's own backend (control_service.py) is a separate
// service from core -- not the same origin restreamer-ui itself is served
// from -- so this is a genuine cross-origin call, authenticated by
// forwarding the operator's own restreamer session token (see
// restreamer.js's GetAccessToken()). control_service.py's dynamic CORS
// middleware allows this specific origin because it recognizes it as this
// tenant's own core_base_url.
//
// AIRCHAIN_API_BASE stays a build-time env var (REACT_APP_-prefixed, per Create
// React App convention): every customer's UI reaches the same platform
// control service at api.<domain>.
//
// AIRCHAIN_TENANT_ID is NOT baked any more -- it is discovered at runtime from
// the hostname the bundle was loaded from, mirroring the window.location logic
// index.js already uses for core's own address. Each customer's copy of
// restreamer-ui is served by that customer's own core at
// <tenant_id>.<domain>/ui/, so the leftmost label *is* the tenant id, and a
// single image serves every tenant without a per-tenant rebuild. The platform's
// own hostnames (apex, www, api, media, ingest, srt) and any non-public host
// (localhost, a LAN IP, the dev host) fall back to the build-time default,
// which is tenant-0 -- the pre-existing instance this UI belonged to before
// multi-tenancy. Anything that isn't a plain single label also falls back, so
// an unexpected hostname can never silently address a different tenant's
// backend.
const AIRCHAIN_API_BASE = process.env.REACT_APP_AIRCHAIN_API_BASE || '';
const PLATFORM_HOST_LABELS = ['www', 'api', 'media', 'ingest', 'srt'];
const TENANT_DOMAIN_SUFFIX =
	process.env.REACT_APP_TENANT_DOMAIN_SUFFIX || 'airchain.tv';

function tenantIdFromHostname() {
	try {
		const host = String(window.location.hostname || '').toLowerCase();
		const suffix = '.' + TENANT_DOMAIN_SUFFIX;
		if (host === TENANT_DOMAIN_SUFFIX || !host.endsWith(suffix)) {
			return null;
		}
		const label = host.slice(0, -suffix.length);
		if (!label || label.includes('.') || PLATFORM_HOST_LABELS.includes(label)) {
			return null;
		}
		return /^[a-z0-9][a-z0-9_-]*$/.test(label) ? label : null;
	} catch (e) {
		return null;
	}
}

const AIRCHAIN_TENANT_ID =
	tenantIdFromHostname() ||
	process.env.REACT_APP_AIRCHAIN_TENANT_ID ||
	'tenant-0';

async function request(method, path, token, body) {
	const headers = { Authorization: `Bearer ${token}` };
	let requestBody;
	if (body !== undefined) {
		headers['Content-Type'] = 'application/json';
		requestBody = JSON.stringify(body);
	}

	const response = await fetch(`${AIRCHAIN_API_BASE}/t/${AIRCHAIN_TENANT_ID}/api${path}`, {
		method,
		headers,
		body: requestBody,
	});

	if (!response.ok) {
		let detail = response.statusText;
		try {
			const body = await response.json();
			detail = body.detail || detail;
		} catch (e) {
			// non-JSON error body -- keep statusText
		}
		const error = new Error(`Airchain API ${method} ${path} failed: ${response.status} ${detail}`);
		// .status/.detail (not just baked into .message) so callers can
		// branch on the real HTTP status without parsing the message
		// string -- added specifically so Captions.js can tell a 402
		// (trial exhausted, see control_service.py's trial gate) apart
		// from any other failure and show an upgrade prompt instead of a
		// generic error.
		error.status = response.status;
		error.detail = detail;
		throw error;
	}

	if (response.status === 204) {
		return null;
	}

	return response.json();
}

const AirchainAPI = {
	CreateScriptImport: (token, body) => request('POST', '/scripts/imports', token, body),
	CommitScriptImport: (importId, token, body) =>
		request('POST', `/scripts/imports/${encodeURIComponent(importId)}/commit`, token, body),
	ListScripts: (token) => request('GET', '/scripts', token),
	GetScriptRundown: (channelid, token) =>
		request('GET', `/channels/${encodeURIComponent(channelid)}/script-following/rundown`, token),
	SaveScriptRundown: (channelid, token, body) =>
		request('PUT', `/channels/${encodeURIComponent(channelid)}/script-following/rundown`, token, body),
	GetScriptFollowing: (channelid, token) =>
		request('GET', `/channels/${encodeURIComponent(channelid)}/script-following`, token),
	PutScriptGateway: (channelid, token, body) =>
		request('PUT', `/channels/${encodeURIComponent(channelid)}/script-following/gateway`, token, body),
	DeleteScriptGateway: (channelid, token) =>
		request('DELETE', `/channels/${encodeURIComponent(channelid)}/script-following/gateway`, token),
	PostScriptControl: (channelid, token, body) =>
		request('POST', `/channels/${encodeURIComponent(channelid)}/script-following/control`, token, body),
	// Returns {state: 'off'|'starting'|'live'|'error', detail?: string}
	GetCaptionsStatus: (channelid, token) => request('GET', `/channels/${channelid}/captions`, token),

	// control_service.py's enable endpoint takes a JobConfig body -- every
	// field has a server-side default, so {} (formatting/STT defaults) is
	// a valid, complete request. A future settings UI could pass overrides
	// here without any change to this wrapper.
	EnableCaptions: (channelid, token) => request('POST', `/channels/${channelid}/captions/enable`, token, {}),

	// No body -- control_service.py's disable endpoint takes none.
	DisableCaptions: (channelid, token) => request('POST', `/channels/${channelid}/captions/disable`, token),

	// Returns a complete JobConfig -- the operator's last-saved settings,
	// or a hardcoded default (deepgram, en-US, pop-on) if they've never
	// saved any. Always a full object, never partial, so the settings
	// form can populate itself directly from the response.
	GetCaptionsSettings: (channelid, token) => request('GET', `/channels/${channelid}/captions/settings`, token),

	// Always succeeds while captions are off/error. While live, only
	// transcription_provider/all_caps (see Captions.js's own
	// LIVE_EDITABLE_FIELDS, kept in sync with control_service.py's
	// STT_ONLY_HOT_SWAP_FIELDS/INJECTOR_LIVE_UPDATABLE_FIELDS) succeed --
	// anything else in the body while live is a 409, so Captions.js must
	// only include live-editable keys in `body` when captions are live,
	// not the full settings object. body is a partial JobConfig; omitted
	// fields keep their previously-saved (or default) value server-side.
	SaveCaptionsSettings: (channelid, token, body) =>
		request('PUT', `/channels/${channelid}/captions/settings`, token, body),

	// Creates (or returns) the channel's stable, encrypted strict-SRT intake.
	// The URL is customer-facing; its listener binding remains server-owned.
	ProvisionStrictIngest: (channelid, token) =>
		request('POST', `/channels/${channelid}/strict-ingest`, token, {}),

	// SIGNUP_PLAN.md: creates a Stripe Checkout Session for this tenant to
	// add a payment method, called when EnableCaptions/SaveCaptionsSettings
	// comes back 402 (free trial exhausted, no stripe_customer_id on file --
	// see control_service.py's trial gate). Returns {checkout_url} -- the
	// caller redirects the browser there directly; there's no in-app
	// payment form, Stripe's own hosted Checkout page owns that entirely.
	CreateBillingCheckout: (token) => request('POST', '/billing/checkout', token),

	// CHANNEL_LIMIT_PLAN.md Phase 3: {max_channels, current_count, at_limit,
	// checked_at} -- ChannelList.js polls this to decide whether "Add new
	// channel" should be enabled. Purely advisory (the real enforcement is
	// control_service's own periodic sweep, independent of this call ever
	// happening) -- see control_service.py's t_channel_limit docstring.
	GetChannelLimit: (token) => request('GET', '/channel-limit', token),

	// This tenant's control-service config. The Wizard's RTMP server/SRT
	// server tiles (src/views/Edit/Wizard/Sources/InternalRTMP.js,
	// InternalSRT.js, via mediamtx.js) use the shared MediaMTX
	// ingest fields it returns -- mediamtx_public_host plus the per-protocol
	// public ports (mediamtx_public_{rtmp,srt}_port) a customer publishes
	// to, and mediamtx_internal_host/mediamtx_internal_srt_port that this
	// tenant's own core pulls that stream back from (always SRT) -- to build
	// the customer-facing publish URL and core's own ingest address without
	// the operator typing either by hand. Returns the full config object; a
	// deployment with no MediaMTX instance configured leaves the mediamtx_*
	// fields empty, which is how the Wizard decides not to offer the option.
	GetConfig: (token) => request('GET', '/config', token),

	// MULTI_USER_PLAN.md: who can log into this tenant. Returns
	// {me: {auth0_sub, role}, members: [...], invitations: [...]} -- `me` is
	// how Team.js knows which row is the operator's own and whether to render
	// the owner-only controls at all; the browser never sees the claims
	// inside its own core session token, so there is no client-side way to
	// work that out. A non-owner's invitations carry no token/accept_url
	// (the token IS the grant -- see control_service.py's _invitation_public).
	GetMembers: (token) => request('GET', '/members', token),

	// {email, role} in, the created invitation (including accept_url) out.
	// v1 delivery is the owner copying that link and sending it themselves:
	// this deployment has no mail infrastructure, so nothing is emailed.
	CreateInvitation: (token, body) => request('POST', '/members/invitations', token, body),

	// invitation is the invitation's own token, not the session token --
	// hence the two-argument shape, session first like every call above.
	RevokeInvitation: (token, invitation) =>
		request('DELETE', `/members/invitations/${encodeURIComponent(invitation)}`, token),

	// Auth0 subs contain a literal "|" (e.g. "auth0|507f1f..."), so the path
	// segment must be encoded, not interpolated raw.
	RemoveMember: (token, auth0Sub) => request('DELETE', `/members/${encodeURIComponent(auth0Sub)}`, token),
};

export { AIRCHAIN_TENANT_ID };

export default AirchainAPI;
